import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShoppingBag } from "lucide-react";
import PageShell from "@/components/PageShell";
import CheckoutHeader from "@/components/CheckoutHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getApiUrl } from "@/utils/api";
import { useBasket } from "@/context/BasketContext";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import ShopperAuthCard from "@/components/ShopperAuthCard";
import { priceForInterval, formatIntervalPrice, isFreeProduct, INTERVAL_LABELS, INTERVAL_SUFFIX, symbolFor } from "@/utils/priceIntervals";

const API = getApiUrl();

export default function CheckoutPage() {
  const basket = useBasket();
  const { shopper, updateProfile } = useShopperAuth();
  const [customer, setCustomer] = useState({
    name: "", email: "", phone: "",
    address: { line1: "", line2: "", city: "", postcode: "", country: "" },
  });
  const [products, setProducts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);   // {code, discount, kind, value}
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/products`).then(({ data }) => {
      const byId = {};
      (data.products || []).forEach((p) => { byId[p.product_id] = p; });
      setProducts(byId);
    }).catch(() => {});
  }, []);

  // Prefill the customer form from the signed-in shopper, without clobbering
  // anything the shopper has already started typing.
  useEffect(() => {
    if (!shopper) return;
    const savedAddr = shopper.billing_address || {};
    setCustomer((prev) => ({
      name: prev.name || shopper.name || "",
      email: prev.email || shopper.email || "",
      phone: prev.phone || shopper.phone || "",
      address: {
        line1: prev.address.line1 || savedAddr.line1 || "",
        line2: prev.address.line2 || savedAddr.line2 || "",
        city: prev.address.city || savedAddr.city || "",
        postcode: prev.address.postcode || savedAddr.postcode || "",
        country: prev.address.country || savedAddr.country || "",
      },
    }));
  }, [shopper]);

  const lines = basket.items.map((i) => ({ ...i, product: products[i.product_id] })).filter((l) => l.product);
  const subtotal = lines.reduce((s, l) => s + priceForInterval(l.product, l.billing_interval), 0);
  const currency = lines[0]?.product?.currency || "GBP";
  const symbol = symbolFor(currency);
  const currentInterval = (lines.find((l) => !isFreeProduct(l.product)) || lines[0])?.billing_interval || "one_off";
  const intervalSuffix = INTERVAL_SUFFIX[currentInterval] || "";
  const ctaVerb = currentInterval === "one_off" ? "Pay" : "Subscribe";
  const discount = promoApplied ? Number(promoApplied.discount || 0) : 0;
  const total = Math.max(0, subtotal - discount);

  const applyPromo = async () => {
    if (!promoCode.trim()) { setPromoError("Enter a code"); return; }
    setPromoBusy(true); setPromoError("");
    try {
      const { data } = await axios.post(`${API}/discount/validate`, {
        code: promoCode.trim(),
        subtotal,
      });
      setPromoApplied(data);
      toast.success(`${data.code} applied — you save ${symbol}${Number(data.discount).toFixed(2)}`);
    } catch (e) {
      setPromoApplied(null);
      setPromoError(e.response?.data?.detail || "Code not accepted");
    } finally { setPromoBusy(false); }
  };
  const removePromo = () => { setPromoApplied(null); setPromoCode(""); setPromoError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!customer.name.trim() || !customer.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (basket.count === 0) { toast.error("Basket is empty"); return; }
    try {
      setSubmitting(true);
      // Persist phone + billing address to the signed-in shopper's profile so
      // next time this is a one-click checkout. Await it (not fire-and-forget)
      // so the write completes before the Stripe redirect nukes the tab.
      if (shopper) {
        try {
          await updateProfile({
            name: customer.name,
            phone: customer.phone,
            billing_address: customer.address,
          });
        } catch { /* non-critical — don't block the checkout */ }
      }
      const contextPropertyId = (() => {
        try { return sessionStorage.getItem("context_property_id") || null; } catch { return null; }
      })();
      const { data } = await axios.post(`${API}/checkout/session`, {
        items: basket.items,
        origin_url: window.location.origin,
        customer,
        promo_code: promoApplied ? promoApplied.code : null,
        context_property_id: contextPropertyId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  if (basket.count === 0) {
    return (
      <PageShell chrome>
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <h1 className="text-2xl font-serif mb-3">Your basket is empty</h1>
          <Link to="/" className="text-primary underline text-sm">Continue shopping</Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell chrome>
      {/* Sticky mini-basket — thin summary bar that stays at the top so
          buyers keep sight of totals while filling in the long form. Hides
          itself when the basket is empty (handled by the early return
          above). Uses `sticky top-0` so it plays nicely with PageShell's
          scroll container. */}
      <div
        className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70"
        data-testid="checkout-mini-basket"
      >
        <div className="max-w-4xl mx-auto px-6 py-2.5 flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-primary shrink-0" />
            <span className="font-medium">
              {basket.count} {basket.count === 1 ? "item" : "items"} in basket
            </span>
          </div>
          <div className="flex items-baseline gap-3">
            {discount > 0 && (
              <span className="text-xs text-muted-foreground line-through hidden sm:inline">
                {symbol}{subtotal.toFixed(2)}
              </span>
            )}
            <span className="font-serif text-lg" data-testid="checkout-mini-basket-total">
              {symbol}{total.toFixed(2)}{intervalSuffix}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12" data-testid="checkout-page">
        <CheckoutHeader title="Checkout" />

        <div className="grid md:grid-cols-3 gap-8">
          <form className="md:col-span-2 space-y-4 bg-background border rounded p-5" onSubmit={submit}>
            <ShopperAuthCard />
            <h2 className="font-semibold">Your details</h2>
            <div>
              <Label>Full name *</Label>
              <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} required data-testid="checkout-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} required data-testid="checkout-email" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} data-testid="checkout-phone" />
            </div>

            <div className="pt-2 border-t">
              <h2 className="font-semibold mb-2">Billing address</h2>
              {shopper && (
                <p className="text-xs text-muted-foreground mb-3" data-testid="checkout-address-remember">
                  We&apos;ll remember this address on <strong>{shopper.email}</strong> so your next checkout is one click.
                </p>
              )}
              <div className="grid gap-3">
                <div>
                  <Label>Address line 1</Label>
                  <Input value={customer.address.line1} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, line1: e.target.value } })} data-testid="checkout-addr-line1" />
                </div>
                <div>
                  <Label>Address line 2 <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Input value={customer.address.line2} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, line2: e.target.value } })} data-testid="checkout-addr-line2" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>City / Town</Label>
                    <Input value={customer.address.city} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, city: e.target.value } })} data-testid="checkout-addr-city" />
                  </div>
                  <div>
                    <Label>Postcode</Label>
                    <Input value={customer.address.postcode} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, postcode: e.target.value.toUpperCase() } })} data-testid="checkout-addr-postcode" />
                  </div>
                </div>
                <div>
                  <Label>Country</Label>
                  <Input value={customer.address.country} onChange={(e) => setCustomer({ ...customer, address: { ...customer.address, country: e.target.value } })} placeholder="United Kingdom" data-testid="checkout-addr-country" />
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">You&apos;ll be redirected to Stripe&apos;s secure checkout to complete payment.</p>
            {currentInterval !== "one_off" && (
              <p className="text-xs text-primary" data-testid="checkout-subscription-notice">
                This is a recurring {INTERVAL_LABELS[currentInterval]?.toLowerCase()} subscription — you&apos;ll be charged
                {" "}{symbol}{total.toFixed(2)}{intervalSuffix} until you cancel.
              </p>
            )}
            <Button type="submit" disabled={submitting} className="w-full" data-testid="checkout-submit">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Redirecting…</> : `${ctaVerb} ${symbol}${total.toFixed(2)}${intervalSuffix}`}
            </Button>
          </form>

          <aside className="border rounded p-4 h-fit bg-background space-y-3">
            <h2 className="font-semibold">Order summary</h2>
            {lines.map((l) => (
              <div key={l.product_id} className="flex justify-between text-sm">
                <span className="truncate pr-2">{l.product.name}</span>
                <span className="whitespace-nowrap">{formatIntervalPrice(l.product, l.billing_interval, currency)}</span>
              </div>
            ))}

            {/* Discount code */}
            <div className="border-t pt-3 space-y-2">
              {promoApplied ? (
                <div className="flex items-center justify-between text-sm bg-green-50 border border-green-200 rounded px-3 py-2" data-testid="promo-applied">
                  <div>
                    <div className="font-mono font-semibold text-green-800">{promoApplied.code}</div>
                    <div className="text-xs text-green-700">
                      {promoApplied.kind === "percent" ? `${promoApplied.value}% off` : `${symbol}${Number(promoApplied.value).toFixed(2)} off`}
                    </div>
                  </div>
                  <button type="button" onClick={removePromo} className="text-xs text-red-600 hover:underline">Remove</button>
                </div>
              ) : (
                <>
                  <Label className="text-xs">Have a discount code?</Label>
                  <div className="flex gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(""); }}
                      placeholder="Enter code"
                      className="font-mono uppercase h-9"
                      data-testid="checkout-promo-input"
                    />
                    <Button type="button" size="sm" variant="outline" onClick={applyPromo} disabled={promoBusy} data-testid="checkout-promo-apply">
                      {promoBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                  {promoError && <p className="text-xs text-red-600" data-testid="promo-error">{promoError}</p>}
                </>
              )}
            </div>

            <div className="border-t pt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{symbol}{subtotal.toFixed(2)}{intervalSuffix}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount</span>
                  <span>−{symbol}{discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-serif text-lg pt-1">
                <span>Total</span>
                <span>{symbol}{total.toFixed(2)}{intervalSuffix}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}
